import React, { useEffect, useMemo, useState } from 'react'
import { CardBody, Card, Col, Row, Input, Label, Button, Form, CardHeader, CardFooter } from "reactstrap";
import apiGlobal from '../../global/api.global';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../';
import { checkEmptyStringValue, checkValidArray, handleServerResponse, isConfigurationButtonDisabled } from 'GenericForms/Helper';
import { useQuery } from 'react-query';
import { queryKeyes } from 'shared/queryKeys';
import { Get, loadEngineTypes } from 'VesselMaster/vesselMaster.hooks';
import { FormikProps, useFormik } from 'formik';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import ErrorComponent from 'Components/ErrorComponent';
import * as Yup from "yup";
import { EngineTypeConstant, FuelTypes, VesselConfigrationTabs, VesselMachineryConstant } from 'shared/constants';
import { commonValidationMessages } from 'Components/ValidationErrorMessages';
import ErrorTooltip from 'Components/ErrorTooltip';
import env from 'environment_system/env_system';
import { queryClient } from 'react-query/queryClient';
import Select from 'react-select';
import { customStyle } from 'shared/CommonCSS';
import isEqual from "fast-deep-equal";
import Message from 'Components/Message';
import FormLoading from 'Components/FormLoading';
import { getConfigurationLimitsAction } from 'Store/Generic/ReportingSlice';

interface EnginesType {
  refreshVesselMachineries: number;
  setRefreshVesselMachineries: (value: number) => void;
  VesselConfActiveTab: number;
  setCheckValuesBeforeSwitch?: (value: boolean) => void;
  setTabName?: (value: string) => void;
  engineFormRef?: any;
  setFormik?: (value: FormikProps<any>) => void;
  formik?: FormikProps<any>;
  setDisableYesButton?: (value: boolean) => void;
  saveBeforeChangePopUp?: boolean;
}

const Engines = ({
  refreshVesselMachineries,
  setRefreshVesselMachineries,
  VesselConfActiveTab,
  setCheckValuesBeforeSwitch,
  setTabName,
  engineFormRef,
  setFormik,
  setDisableYesButton,
  saveBeforeChangePopUp,
}: EnginesType) => {
  /** State variables start */
  const { VesselID, Vessels, ConfigurationLimits, reportingLoading, reportingError } = useSelector((state: RootState) => state.Reporting);
  const vessel = Vessels.find((rec: any) => rec.id === VesselID);
  const [vesselFuelIds, setVesselFuelIds] = useState<Array<number>>([]);
  const [mainEngineSpinner, setMainEngineSpinner] = useState<boolean>(false);
  const [auxEngineSpinner, setAuxEngineSpinner] = useState<boolean>(false);
  const [spinnerIndex, setSpinnerIndex] = useState<number | null>();
  const [formRefreshKey, setFormRefreshKey] = useState<number>(0);
  const [engineTypes, setEngineTypes] = useState<any[]>([]);
  const MainEngineObject = ConfigurationLimits?.main_engine_setting ?? [];
  const VesselMachinery = ConfigurationLimits?.vessel_machinery_list ?? [];
  const VesselFuelTypes = useMemo(
    () => ConfigurationLimits?.vessel_fuel_information ?? [],
    [ConfigurationLimits]);
  const dispatch = useDispatch();
  /** State variables end */

  /** Queries */
  /** load auxillary engine data*/
  const {
    data: AuxillaryEngineObject,
    isLoading: AuxillaryEngineLoading,
  }: { data: any[]; isLoading: any; isError: any } = useQuery(
    [queryKeyes.vessel.AuxillaryMachineSetting.key, VesselID],
    async () => {
      return await Get(queryKeyes.vessel.AuxillaryMachineSetting.url(VesselID), VesselID);
    },
    {
      enabled: true,
      staleTime: Infinity,
    }
  );
  /** Load engine type master for 2 stroke engines */
  const {
    data: EngineTypeMaster2Stroke,
    isLoading: EngineTypeMaster2StrokeLoading,
    isError: EngineTypeMaster2StrokeError,
  } = useQuery(
    [queryKeyes.masters.EngineTypeMaster.key, VesselID, vesselFuelIds],
    async () => {
      return await loadEngineTypes(VesselID, vesselFuelIds, "2_stroke");
    },
    {
      enabled: true,
      staleTime: Infinity,
    }
  );  /** Load engine type master for 4 stroke engines */
  const {
    data: EngineTypeMaster4Stroke,
    isLoading: EngineTypeMaster4StrokeLoading,
    isError: EngineTypeMaster4StrokeError,
  } = useQuery(
    [queryKeyes.masters.EngineTypeMaster.key, VesselID, vesselFuelIds],
    async () => {
      return await loadEngineTypes(VesselID, vesselFuelIds, "4_stroke");
    },
    {
      enabled: true,
      staleTime: Infinity,
    }
  );
  /** Queries end */

  /**Initial values start */
  /** Assign initial values to Engines formik object */
  const getMainEngineInitialValues = () => {
    let mainEngineArr: any[] = [];
    let mainEngineIds: Set<number> = new Set(); // Use a Set to avoid duplicates
    const VesselMEArr = VesselMachinery?.filter((item: any) =>
      item?.machinery_precedence_id === VesselMachineryConstant.MAIN_ENGINE ||
      item?.machinery_precedence_id === VesselMachineryConstant.STEAM_TURBINE_PROPULSION
    );
    if (MainEngineObject?.length > 0) {
      VesselMEArr?.forEach((MachinaryObj: any) => {
        // Find the matching engine in MainEngine
        const matchingEngine = MainEngineObject.find(
          (engineObj: any) => engineObj.vessel_machinery === MachinaryObj?.id
        );
        if (matchingEngine && !mainEngineIds.has(matchingEngine.vessel_machinery)) {
          // Add the matching engine
          mainEngineArr.push(matchingEngine);
          mainEngineIds.add(matchingEngine.vessel_machinery);
        } else if (!mainEngineIds.has(MachinaryObj.id)) {
          // Add a default object if no matching engine is found
          mainEngineArr.push({
            vessel_machinery_name: MachinaryObj.vessel_machinery_name,
            engine_type: MachinaryObj.machinery_precedence_id === VesselMachineryConstant.STEAM_TURBINE_PROPULSION ? "NA" : "2_stroke",
            propeller_type: "controllable_pitch_propeller",
            engine_type_master:
              MachinaryObj.machinery_precedence_id === VesselMachineryConstant.STEAM_TURBINE_PROPULSION ?
                engineTypes?.filter((item: any) => item.precedenece_id === EngineTypeConstant.NA)[0]?.id : null,
            engine_type_name: MachinaryObj.machinery_precedence_id === VesselMachineryConstant.STEAM_TURBINE_PROPULSION ? "NA" : null,
            mcr_kw: null,
            mcr_rpm: null,
            shaft_power: 0,
            propeller_pitch: null,
            eedi: 0,
            eexi: 0,
            vessel: VesselID,
            vessel_machinery: MachinaryObj.id,
            machinery_precedence_id: MachinaryObj.machinery_precedence_id,
          });
          mainEngineIds.add(MachinaryObj.id);
        }
      });
    } else {
      // Add default objects for all machinery if MainEngine is empty
      VesselMEArr?.forEach((MachinaryObj: any) => {
        mainEngineArr.push({
          vessel_machinery_name: MachinaryObj.vessel_machinery_name,
          engine_type: MachinaryObj.machinery_precedence_id === VesselMachineryConstant.STEAM_TURBINE_PROPULSION ? "NA" : "2_stroke",
          propeller_type: "controllable_pitch_propeller",
          engine_type_master:
            MachinaryObj.machinery_precedence_id === VesselMachineryConstant.STEAM_TURBINE_PROPULSION ?
              engineTypes?.filter((item: any) => item.precedenece_id === EngineTypeConstant.NA)[0]?.id : null,
          engine_type_name: MachinaryObj.machinery_precedence_id === VesselMachineryConstant.STEAM_TURBINE_PROPULSION ? "NA" : null,
          mcr_kw: null,
          mcr_rpm: null,
          shaft_power: 0,
          propeller_pitch: null,
          eedi: 0,
          eexi: 0,
          vessel: VesselID,
          vessel_machinery: MachinaryObj.id,
          machinery_precedence_id: MachinaryObj.machinery_precedence_id,
        });
      });
    }
    return mainEngineArr;
  };

  const getAuxillaryEngineInitialValues = () => {
    let AuxillaryEngineArr: any[] = [];
    let auxIds: Set<number> = new Set(); // Use a Set to avoid duplicates
    const VesselAEArr = VesselMachinery?.filter((item: any) =>
      item?.machinery_precedence_id === VesselMachineryConstant.AUXILIARY_ENGINE ||
      item?.machinery_precedence_id === VesselMachineryConstant.STEAM_TURBINE_GENERATOR
    );
    if (AuxillaryEngineObject?.length > 0 && AuxillaryEngineObject[0]?.id > 0) {
      VesselAEArr?.forEach((MachinaryObj: any) => {
        // Find the matching engine in AuxillaryEngine
        const matchingEngine = AuxillaryEngineObject.find(
          (engineObj) => engineObj.vessel_machinery === MachinaryObj?.id
        );
        if (matchingEngine && !auxIds.has(matchingEngine.vessel_machinery)) {
          // Add the matching engine
          AuxillaryEngineArr.push(matchingEngine);
          auxIds.add(matchingEngine.vessel_machinery);
        } else if (!auxIds.has(MachinaryObj.id)) {
          // Add a default object if no matching engine is found
          AuxillaryEngineArr.push({
            vessel_machinery_name: MachinaryObj.vessel_machinery_name,
            power_output_rating: null,
            eedi: 0,
            rated_voltage: 0,
            rated_frequency: 0,
            rated_rpm: 0,
            alternator_power_rating: 0,
            vessel: VesselID,
            vessel_machinery: MachinaryObj.id,
            engine_type:
              MachinaryObj.machinery_precedence_id === VesselMachineryConstant.STEAM_TURBINE_GENERATOR ?
                engineTypes?.filter((item: any) => item.precedenece_id === EngineTypeConstant.NA)[0]?.id : null,
            engine_type_name: MachinaryObj.machinery_precedence_id === VesselMachineryConstant.STEAM_TURBINE_GENERATOR ? "NA" : null,
            machinery_precedence_id: MachinaryObj.machinery_precedence_id,
          });
          auxIds.add(MachinaryObj.id);
        }
      });
    } else {
      // Add default objects for all machinery if AuxillaryEngine is empty
      VesselAEArr?.forEach((MachinaryObj: any) => {
        AuxillaryEngineArr.push({
          vessel_machinery_name: MachinaryObj.vessel_machinery_name,
          power_output_rating: null,
          eedi: 0,
          rated_voltage: 0,
          rated_frequency: 0,
          rated_rpm: 0,
          alternator_power_rating: 0,
          vessel: VesselID,
          vessel_machinery: MachinaryObj.id,
          engine_type:
            MachinaryObj.machinery_precedence_id === VesselMachineryConstant.STEAM_TURBINE_GENERATOR ?
              engineTypes?.filter((item: any) => item.precedenece_id === EngineTypeConstant.NA)[0]?.id : null,
          engine_type_name: MachinaryObj.machinery_precedence_id === VesselMachineryConstant.STEAM_TURBINE_GENERATOR ? "NA" : null,
          machinery_precedence_id: MachinaryObj.machinery_precedence_id,
        });
      });
    }
    return AuxillaryEngineArr;
  };
  /**Inital values end */
  /** useEffect */
  useEffect(() => {
    EngineFormik.initialValues.mainEngine =
      getMainEngineInitialValues();
    EngineFormik.initialValues.auxillaryEngine =
      getAuxillaryEngineInitialValues();
    setFormRefreshKey(formRefreshKey + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [AuxillaryEngineObject, VesselMachinery]);
  useEffect(() => {
    let arr: any[] = [];
    if (VesselFuelTypes && VesselFuelTypes?.length > 0) {
      VesselFuelTypes?.forEach((fuelType: { [key: string]: string | number | Date | boolean }) => {
        arr.push(fuelType?.fuel_type_precedence_id);
      })
      setVesselFuelIds(arr);
    }
  }, [VesselFuelTypes, VesselID]);
  useEffect(() => {
    loadEngineTypes(VesselID)
      .then((res) => {
        setEngineTypes(res.data);
      }).catch((err) => {
        console.error(err);
      })
  }, [EngineTypeMaster2Stroke, EngineTypeMaster4Stroke, VesselID]);
  /** useEffect end */

  // General Settings Formik Object
  const EngineFormik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      mainEngine: getMainEngineInitialValues(),
      auxillaryEngine: getAuxillaryEngineInitialValues(),
    },
    validationSchema: Yup.object().shape({
      mainEngine: Yup.array(
        Yup.object().shape({
          engine_type: Yup.string().oneOf([
            "2_stroke",
            "4_stroke",
            "steam_turbine_type",
          ]),
          propeller_type: Yup.string().oneOf([
            "controllable_pitch_propeller",
            "fixed_pitch_propeller",
          ]),
          shaft_power: Yup.string()
            .matches(/^\d*\.?\d*$/, commonValidationMessages.positive)
            .nullable(),
          propeller_pitch: Yup.string()
            .matches(
              /^(?!.*\s)\d{1,2}(\.\d{1,10})?$/,
              commonValidationMessages.before2after10
            )
            .when("propeller_type", {
              is: "fixed_pitch_propeller",
              then: (schema: any) =>
                schema.required(commonValidationMessages.required),
              otherwise: (schema: any) => schema.nullable(),
            }),
          eedi: Yup.string()
            .matches(
              /^\d{1,4}(\.\d{0,4})?$/,
              commonValidationMessages.before4after4
            )
            .nullable(),
          eexi: Yup.string()
            .matches(
              /^\d{1,5}(\.\d{0,4})?$/,
              commonValidationMessages.before5after4
            )
            .nullable(),
          mcr_kw: Yup.string()
            .matches(
              /^\d{1,6}(\.\d{0,4})?$/,
              commonValidationMessages.before6after4
            )
            .nullable(),
          mcr_rpm: Yup.string()
            .matches(
              /^\d{1,5}(\.\d{0,4})?$/,
              commonValidationMessages.before5after4
            )
            .nullable(),
        })
      ),
      auxillaryEngine: Yup.array(
        Yup.object().shape({
          power_output_rating: Yup.string()
            .matches(
              /^\d{1,5}(\.\d{0,4})?$/,
              commonValidationMessages.before5after4
            )
            .required(commonValidationMessages.required),
          rated_voltage: Yup.string()
            .matches(
              /^\d{1,5}(\.\d{0,4})?$/,
              commonValidationMessages.before5after4
            )
            .nullable(),
          rated_frequency: Yup.string()
            .matches(
              /^\d{1,5}(\.\d{0,4})?$/,
              commonValidationMessages.before5after4
            )
            .nullable(),
          rated_rpm: Yup.string()
            .matches(
              /^\d{1,5}(\.\d{0,4})?$/,
              commonValidationMessages.before5after4
            )
            .nullable(),
          alternator_power_rating: Yup.string()
            .matches(
              /^\d{1,5}(\.\d{0,4})?$/,
              commonValidationMessages.before5after4
            )
            .nullable(),
          engine_type: Yup.number()
            .when(
              "$fieldVisibility",
              (fieldVisibility: any, schema) => {
                return (!vesselFuelIds?.includes(FuelTypes.LNG_BUNKER) && !vesselFuelIds?.includes(FuelTypes.LNG_CARGO))
                  ? schema.nullable()
                  : schema.required(commonValidationMessages.required);
              }
            ),
        })
      ),
    }),
    onSubmit: async () => {
      let data: any;
      if (EngineFormik?.values?.mainEngine) {
        EngineFormik?.values?.mainEngine.forEach(async (item: any) => {
          await handleEngineSubmit(item, 'main_engine_setting', 'MainEngine', EngineFormik.errors, true);
        });
      }
      if (EngineFormik?.values?.auxillaryEngine) {
        EngineFormik?.values?.auxillaryEngine.forEach(async (item: any) => {
          await handleEngineSubmit(item, 'auxillary_machine_setting', 'AuxillaryEngine', EngineFormik.errors, true);
        });
      }
      if (await data) {
        return await data;
      }
    }
  });

  const tabName = 'Engines';
  const handleFormikStateChange = (values: any, initialValues: any) => {
    const areValuesEqual = isEqual(values, initialValues);
    setCheckValuesBeforeSwitch(areValuesEqual);
    setTabName(tabName);
    return areValuesEqual;
  };

  useEffect(() => {
    EngineFormik.initialValues.mainEngine = getMainEngineInitialValues();
    EngineFormik.values.mainEngine = getMainEngineInitialValues();
    EngineFormik.initialValues.auxillaryEngine = getAuxillaryEngineInitialValues();
    EngineFormik.values.auxillaryEngine = getAuxillaryEngineInitialValues();
    setRefreshVesselMachineries(refreshVesselMachineries + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [VesselFuelTypes, FuelTypes, VesselID]);


  useEffect(() => {
    handleFormikStateChange(EngineFormik?.values, EngineFormik?.initialValues);
    if (engineFormRef) {
      engineFormRef.current = EngineFormik;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [EngineFormik?.values, EngineFormik?.initialValues]);
  useEffect(() => {
    if (VesselConfActiveTab === VesselConfigrationTabs.ENGINES && EngineFormik?.initialValues !== EngineFormik?.values) {
      Object.keys(EngineFormik?.errors).length > 0 ? setDisableYesButton(true) : setDisableYesButton(false);
      setFormik(EngineFormik); // Example: Assign formik instance for the "Other" tab
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [VesselConfActiveTab, EngineFormik?.values, EngineFormik?.errors]);

  useEffect(() => {
    if (VesselConfActiveTab !== VesselConfigrationTabs.ENGINES) {
      EngineFormik?.setTouched({});
      EngineFormik?.setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [VesselConfActiveTab]);

  const handleEngineSubmit = async (values: any, url: any, engineType: any, errors: any, isFullFormSubmit: boolean = false) => {
    try {
      let responseArray: any[] = [];
      let data: any;
      if (!errors || isFullFormSubmit) {
        if (values?.id) {
          data = apiGlobal.put(`/${url}/${values.id}/`, values);
          responseArray.push(data);
        } else {
          data = apiGlobal.post(`/${url}/`, values);
          responseArray.push(data);
        }
        /** handle server response */
        await handleServerResponse(data).then(async (res) => {
          if (res) {
            await queryClient.invalidateQueries(queryKeyes.vessel.AuxillaryMachineSetting.key);
            await queryClient.invalidateQueries(queryKeyes.vessel.ConfigurationList.key);
            await queryClient.invalidateQueries(queryKeyes.vessel.VesselMachineries.key);
            await queryClient.invalidateQueries(queryKeyes.vessel.MachinaryFuelGroupByVesselId.key);
            await queryClient.invalidateQueries(queryKeyes.vessel.MachinaryEnergyGroupByVesselId.key);
            await queryClient.invalidateQueries(queryKeyes.vessel.VoyageParameterObject.key);
            dispatch(getConfigurationLimitsAction(VesselID as number));
            setRefreshVesselMachineries(refreshVesselMachineries + 1);
            return data
          } else {
            setMainEngineSpinner(false);
            setAuxEngineSpinner(false);
          }
        });
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      console.error();
    }
  };

  useEffect(() => {
    EngineFormik.resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshVesselMachineries]);

  if (AuxillaryEngineLoading ||
    reportingLoading ||
    EngineTypeMaster2StrokeLoading ||
    EngineTypeMaster4StrokeLoading) {
    return <FormLoading />
  }
  if (reportingError || EngineTypeMaster2StrokeError || EngineTypeMaster4StrokeError) {
    return <ErrorComponent message='Error loading required data!' />
  } else {
    return (
      <React.Fragment>
        {(AuxillaryEngineLoading ||
          reportingLoading ||
          EngineTypeMaster2StrokeLoading ||
          EngineTypeMaster4StrokeLoading) && <FormLoading />}
        {(reportingError || EngineTypeMaster2StrokeError || EngineTypeMaster4StrokeError) && (
          <ErrorComponent message="Error loading required data!" />
        )}
        {!(
          AuxillaryEngineLoading ||
          reportingLoading ||
          EngineTypeMaster2StrokeLoading ||
          EngineTypeMaster4StrokeLoading
        ) &&
          !(reportingError || EngineTypeMaster2StrokeError || EngineTypeMaster4StrokeError) &&
          <Form
            onSubmit={EngineFormik.handleSubmit}
            noValidate
            autoComplete="off"
            key={refreshVesselMachineries}
          >
            <React.Fragment>
              <Card className='p-0' >
                <CardHeader className="p-2">
                  <h4 className="mb-0">Main Engines</h4>
                </CardHeader>
                <CardBody>
                  <Row>
                    {/* Handle Main engine form */}
                    {checkValidArray(EngineFormik?.values?.mainEngine) ? (
                      EngineFormik?.values?.mainEngine.map(
                        (engine: any, index: number) =>
                        (
                          <Card className='mb-0' key={`${refreshVesselMachineries}_${index}`}>
                            <CardHeader className="p-2">
                              <h5 className="mb-0">
                                {engine.vessel_machinery_name}
                              </h5>
                            </CardHeader>
                            <CardBody className="p-2">
                              <Row>
                                <Col sm={5}>
                                  <Label className="bold_lbl asteric">Engine type</Label>
                                  <div className="ele_row1">
                                    {/* 2-Stroke Option */}
                                    <div className="form-check">
                                      <Input
                                        onFocus={(e) => e.target.select()}
                                        className="form-check-input"
                                        type="radio"
                                        name={`mainEngine.${index}.engine_type`}
                                        id={`stroke2_${index}`}
                                        value="2_stroke"
                                        checked={
                                          EngineFormik?.values?.mainEngine[index]?.engine_type === "2_stroke"
                                        }
                                        onChange={() => {
                                          EngineFormik.setFieldValue(
                                            `mainEngine.${index}.engine_type`,
                                            "2_stroke"
                                          );
                                        }}
                                        disabled={isConfigurationButtonDisabled(vessel)}
                                      />
                                      <Label className="form-check-label mr-1" htmlFor={`stroke2_${index}`}>
                                        2 stroke
                                      </Label>
                                    </div>
                                    {/* 4-Stroke Option */}
                                    <div className="form-check">
                                      <Input
                                        onFocus={(e) => e.target.select()}
                                        className="form-check-input"
                                        type="radio"
                                        name={`mainEngine.${index}.engine_type`}
                                        id={`stroke4_${index}`}
                                        value="4_stroke"
                                        checked={
                                          EngineFormik?.values?.mainEngine[index]?.engine_type === "4_stroke"
                                        }
                                        onChange={() => {
                                          EngineFormik.setFieldValue(
                                            `mainEngine.${index}.engine_type`,
                                            "4_stroke"
                                          );
                                        }}
                                        disabled={isConfigurationButtonDisabled(vessel)}
                                      />
                                      <Label htmlFor={`stroke4_${index}`}>4 stroke</Label>
                                    </div>
                                  </div>
                                </Col>
                                <Col sm={3}>
                                  <Label className="mb-0" for={`mainEngine.${index}.mcr_kw`}>MCR</Label>
                                  <div className="ele_row1">
                                    <div className="input-group">
                                      <Input
                                        onFocus={(e) => e.target.select()}
                                        type="text"
                                        name={`mainEngine.${index}.mcr_kw`}
                                        id={`mainEngine_${index}_mcr_kw`}
                                        className="form-control text-right"
                                        value={EngineFormik?.values?.mainEngine[index]?.mcr_kw || ""}
                                        onChange={(e) => {
                                          const fieldName = `mainEngine.${index}.mcr_kw`;
                                          checkEmptyStringValue(e, fieldName, EngineFormik?.setFieldValue);
                                        }}
                                        disabled={isConfigurationButtonDisabled(vessel)}
                                      />
                                      <div className="input-group-text small-unit-size">kW</div>
                                    </div>
                                    {
                                      Array.isArray(EngineFormik?.errors?.mainEngine) &&
                                      EngineFormik?.errors?.mainEngine[index]?.mcr_kw &&
                                      env?.form_validation && (
                                        <ErrorTooltip
                                          target={`mainEngine_${index}_mcr_kw`}
                                          message={EngineFormik?.errors?.mainEngine[index]?.mcr_kw}
                                          open={!saveBeforeChangePopUp && VesselConfActiveTab === VesselConfigrationTabs.ENGINES && !!EngineFormik?.errors?.mainEngine[index]?.mcr_kw}
                                        />
                                      )}
                                    <p className="mb-0 mx-2 mt-2">@</p>
                                    <div className="input-group">
                                      <Input
                                        onFocus={(e) => e.target.select()}
                                        type="text"
                                        name={`mainEngine.${index}.mcr_rpm`}
                                        id={`mainEngine_${index}_mcr_rpm`}
                                        className="form-control text-right"
                                        value={EngineFormik?.values?.mainEngine[index]?.mcr_rpm || ""}
                                        onChange={(e) => {
                                          const fieldName = `mainEngine.${index}.mcr_rpm`;
                                          checkEmptyStringValue(e, fieldName, EngineFormik?.setFieldValue);
                                        }}
                                        disabled={isConfigurationButtonDisabled(vessel)}
                                      />
                                      <div className="input-group-text small-unit-size">rpm</div>
                                    </div>
                                    {
                                      Array.isArray(EngineFormik?.errors?.mainEngine) &&
                                      EngineFormik?.errors?.mainEngine[index]?.mcr_rpm &&
                                      env?.form_validation && (
                                        <ErrorTooltip
                                          target={`mainEngine_${index}_mcr_rpm`}
                                          message={EngineFormik?.errors?.mainEngine[index]?.mcr_rpm}
                                          open={!saveBeforeChangePopUp && VesselConfActiveTab === VesselConfigrationTabs.ENGINES && !!EngineFormik?.errors?.mainEngine[index]?.mcr_rpm}
                                        />
                                      )}
                                  </div>
                                </Col>
                                <Col sm={4}>
                                  <div className="ele_row1">
                                    <div>
                                      <Label className="mb-0 small-unit-size" for={`eedi_${index}`}>EEDI<small>(gCO<sub>2</sub>/ton-mile)</small></Label>
                                      <div className="input-group mb-3">
                                        <Input
                                          onFocus={(e) => e.target.select()}
                                          type="text"
                                          className="form-control max-width-7 text-right"
                                          name={`mainEngine.${index}.eedi`}
                                          id={`eedi_${index}`}
                                          value={EngineFormik?.values?.mainEngine[index]?.eedi || ""}
                                          onChange={(e) => {
                                            const fieldName = `mainEngine.${index}.eedi`;
                                            checkEmptyStringValue(e, fieldName, EngineFormik?.setFieldValue);
                                          }}
                                          disabled={isConfigurationButtonDisabled(vessel)}
                                        />
                                        {Array.isArray(EngineFormik?.errors?.mainEngine) &&
                                          EngineFormik?.errors?.mainEngine[index]?.eedi &&
                                          env?.form_validation && (
                                            <ErrorTooltip
                                              target={`eedi_${index}`}
                                              message={EngineFormik?.errors?.mainEngine[index]?.eedi}
                                              open={!saveBeforeChangePopUp && VesselConfActiveTab === VesselConfigrationTabs.ENGINES && !!EngineFormik?.errors?.mainEngine[index]?.eedi}
                                            />
                                          )}
                                      </div>
                                    </div>
                                    <div className="ms-3">
                                      <Label className="mb-0 small-unit-size" for={`eexi_${index}`}>EEXI<small>(gCO<sub>2</sub>/ton-mile)</small></Label>
                                      <div className="input-group mb-3">
                                        <Input
                                          onFocus={(e) => e.target.select()}
                                          type="text"
                                          className="form-control max-width-7 text-right"
                                          name={`mainEngine.${index}.eexi`}
                                          id={`eexi_${index}`}
                                          value={EngineFormik?.values?.mainEngine[index]?.eexi || ""}
                                          onChange={(e) => {
                                            const fieldName = `mainEngine.${index}.eexi`;
                                            checkEmptyStringValue(e, fieldName, EngineFormik?.setFieldValue);
                                          }}
                                          disabled={isConfigurationButtonDisabled(vessel)}
                                        />
                                        {Array.isArray(EngineFormik?.errors?.mainEngine) &&
                                          EngineFormik?.errors?.mainEngine[index]?.eexi &&
                                          env?.form_validation && (
                                            <ErrorTooltip
                                              target={`eexi_${index}`}
                                              message={EngineFormik?.errors?.mainEngine[index]?.eexi}
                                              open={!saveBeforeChangePopUp && VesselConfActiveTab === VesselConfigrationTabs.ENGINES && !!EngineFormik?.errors?.mainEngine[index]?.eexi}
                                            />
                                          )
                                        }
                                      </div>
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                              <Row>
                                <Col sm={5}>
                                  {/* Propeller Type Label */}
                                  <Label className="bold_lbl mb-2 asteric">Propeller Type</Label>
                                  <div className="ele_row1">
                                    {/* Controllable Pitch Propeller */}
                                    <div className="form-check">
                                      <Input
                                        onFocus={(e) => e.target.select()}
                                        name={`mainEngine.${index}.propeller_type`}
                                        className="form-check-input"
                                        type="radio"
                                        id={`ccpp_${index}`}
                                        value="controllable_pitch_propeller"
                                        checked={
                                          EngineFormik?.values?.mainEngine[index]?.propeller_type ===
                                          "controllable_pitch_propeller"
                                        }
                                        onChange={() =>
                                          EngineFormik.setFieldValue(
                                            `mainEngine.${index}.propeller_type`,
                                            "controllable_pitch_propeller"
                                          )
                                        }
                                        disabled={isConfigurationButtonDisabled(vessel)}
                                      />
                                      <Label
                                        className="form-check-label me-1"
                                        htmlFor={`ccpp_${index}`}
                                      >
                                        Controllable pitch propeller
                                      </Label>
                                    </div>
                                    {/* Fixed Pitch Propeller */}
                                    <div className="form-check">
                                      <Input
                                        onFocus={(e) => e.target.select()}
                                        className="form-check-input"
                                        type="radio"
                                        name={`mainEngine.${index}.propeller_type`}
                                        id={`fpp_${index}`}
                                        value="fixed_pitch_propeller"
                                        checked={
                                          EngineFormik?.values?.mainEngine[index]?.propeller_type ===
                                          "fixed_pitch_propeller"
                                        }
                                        onChange={() =>
                                          EngineFormik.setFieldValue(
                                            `mainEngine.${index}.propeller_type`,
                                            "fixed_pitch_propeller"
                                          )
                                        }
                                        disabled={isConfigurationButtonDisabled(vessel)}
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor={`fpp_${index}`}
                                      >
                                        Fixed pitch propeller
                                      </Label>
                                    </div>
                                  </div>
                                </Col>
                                <Col sm={7} className="">
                                  <div className="ele_row1">
                                    <React.Fragment>
                                      <div>
                                        {EngineFormik?.values?.mainEngine[index]?.propeller_type ===
                                          "fixed_pitch_propeller" && (
                                            <React.Fragment>
                                              <Label
                                                className={`mb-0 ${EngineFormik?.values?.mainEngine[index]?.propeller_type ===
                                                  "fixed_pitch_propeller" && "asteric"
                                                  }`}
                                              >
                                                Pitch
                                              </Label>
                                              <div className="input-group mb-3">
                                                <Input
                                                  onFocus={(e) => e.target.select()}
                                                  type="text"
                                                  className="form-control max-width-7 text-right"
                                                  name={`mainEngine.${index}.propeller_pitch`}
                                                  id={`propeller_pitch_${index}`}
                                                  value={
                                                    EngineFormik?.values?.mainEngine[index]?.propeller_pitch || ""
                                                  }
                                                  onChange={(e) => {
                                                    EngineFormik.setFieldValue(
                                                      `mainEngine.${index}.propeller_pitch`,
                                                      e.target.value
                                                    );
                                                  }}
                                                  disabled={isConfigurationButtonDisabled(vessel)}
                                                />
                                                {EngineFormik?.errors?.mainEngine?.[index]?.propeller_pitch &&
                                                  env?.form_validation && (
                                                    <ErrorTooltip
                                                      target={`propeller_pitch_${index}`}
                                                      message={EngineFormik.errors.mainEngine[index].propeller_pitch}
                                                      open={
                                                        !saveBeforeChangePopUp &&
                                                        VesselConfActiveTab === VesselConfigrationTabs.ENGINES &&
                                                        !!EngineFormik.errors.mainEngine[index].propeller_pitch
                                                      }
                                                    />
                                                  )}
                                                <div className="input-group-text round_border">m</div>
                                              </div>
                                            </React.Fragment>
                                          )}

                                      </div>
                                      <div
                                        className={`${EngineFormik?.values?.mainEngine[index]?.propeller_type ===
                                          "fixed_pitch_propeller" && "ms-3"
                                          }`}
                                      >
                                        <Label className="mb-0" for={`shaft_power_${index}`}>Shaft power</Label>
                                        <div className="input-group mb-3">
                                          <Input
                                            onFocus={(e) => e.target.select()}
                                            type="text"
                                            className="form-control max-width-7 text-right"
                                            name={`mainEngine.${index}.shaft_power`}
                                            id={`shaft_power_${index}`}
                                            value={
                                              EngineFormik?.values?.mainEngine[index]?.shaft_power || ""
                                            }
                                            onChange={(e) => {
                                              EngineFormik.setFieldValue(
                                                `mainEngine.${index}.shaft_power`,
                                                e.target.value
                                              );
                                            }}
                                            disabled={isConfigurationButtonDisabled(vessel)}
                                          />
                                          {EngineFormik?.errors && EngineFormik?.errors?.mainEngine?.[index]?.shaft_power &&
                                            env?.form_validation &&
                                            (
                                              <ErrorTooltip
                                                target={`shaft_power_${index}`}
                                                message={EngineFormik?.errors?.mainEngine?.[index]?.shaft_power}
                                                open={!saveBeforeChangePopUp && VesselConfActiveTab === VesselConfigrationTabs.ENGINES && !!EngineFormik?.errors?.mainEngine?.[index]?.shaft_power}
                                              />
                                            )}
                                          <div className="input-group-text round_border">kW</div>
                                        </div>
                                      </div>
                                    </React.Fragment>
                                    <div className="ms-3">
                                      <Label className="mb-0" htmlFor={`engine_type_master_${index}`}>
                                        Engine category
                                      </Label>
                                      <Select
                                        name={`mainEngine.${index}.engine_type_master`}
                                        inputId={`engine_type_master_${index}`}
                                        options={
                                          EngineFormik?.values && EngineFormik?.values?.mainEngine[index]?.engine_type === '2_stroke' ?
                                            EngineTypeMaster2Stroke : EngineTypeMaster4Stroke
                                        }
                                        getOptionLabel={(e: any) => e.name}
                                        getOptionValue={(e: any) => e.id}
                                        onChange={(e: any) => {
                                          EngineFormik.setFieldValue(`mainEngine.${index}.engine_type_master`, e?.id);
                                          EngineFormik.setFieldValue(`mainEngine.${index}.engine_type_name`, e?.name);
                                        }}
                                        defaultValue={
                                          engine?.engine_type_master &&
                                          {
                                            id: EngineFormik?.values?.mainEngine[index]?.engine_type_master,
                                            name: EngineFormik?.values?.mainEngine[index]?.engine_type_name,
                                          }
                                        }
                                        isDisabled={isConfigurationButtonDisabled(vessel) || engine?.machinery_precedence_id === VesselMachineryConstant.STEAM_TURBINE_PROPULSION}
                                        styles={customStyle}
                                        className="select-height min-width-9-5"
                                      />
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                            </CardBody>
                            <CardFooter className="p-2">
                              <Button
                                type="button"
                                color="primary"
                                className="justify_right"
                                onClick={async () => {
                                  setSpinnerIndex(index)
                                  setMainEngineSpinner(true);
                                  const error = EngineFormik?.errors?.mainEngine?.[index];
                                  const res: any = await handleEngineSubmit(
                                    EngineFormik?.values?.mainEngine[index],
                                    "main_engine_setting",
                                    "MainEngine",
                                    error
                                  );
                                  if (res && res?.data) {
                                    const updatedMainEngine = [...EngineFormik.values.mainEngine];
                                    updatedMainEngine[index] = res.data;
                                    EngineFormik.setValues({
                                      ...EngineFormik.values,
                                      mainEngine: updatedMainEngine,
                                    });
                                    await queryClient.invalidateQueries(queryKeyes.vessel.MainEngineSetting.key);
                                    await queryClient.invalidateQueries(queryKeyes.vessel.ConfigurationList.key);
                                  }
                                  setMainEngineSpinner(false);
                                }}
                                disabled={isConfigurationButtonDisabled(vessel) || (index === spinnerIndex && mainEngineSpinner)}
                              >
                                {mainEngineSpinner &&
                                  index === spinnerIndex &&
                                  <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />} Save
                              </Button>
                            </CardFooter>
                          </Card>
                        ))) : (
                      <Message message='No Main Engine data available. Please add main engine details.' type='warning' width='35rem' />
                    )}
                  </Row>
                </CardBody>
              </Card>
              <Card>
                <CardHeader className="p-2">
                  <h4 className="mb-0">Auxiliary Engine</h4>
                </CardHeader>
                <CardBody>
                  <Row>
                    {EngineFormik?.values?.auxillaryEngine && EngineFormik?.values?.auxillaryEngine?.length > 0 ? (
                      EngineFormik?.values?.auxillaryEngine.map(
                        (auxEngine: any, index: number) => (
                          <Col md="6" key={index}>
                            <Card key={`${refreshVesselMachineries}_${index}`}>
                              <CardHeader className="p-2">
                                <h5 className="mb-0">
                                  {auxEngine.vessel_machinery_name}
                                </h5>
                              </CardHeader>
                              <CardBody className="p-2">
                                <Row>
                                  <Col>
                                    {/* Power Output Rating */}
                                    <Label className="asteric mb-0" for={`power_output_rating_${index}`}>Power output rating</Label>
                                    <div className="input-group mb-3">
                                      <Input
                                        onFocus={(e) => e.target.select()}
                                        type="text"
                                        id={`power_output_rating_${index}`}
                                        className="form-control max-width-7 text-right"
                                        name={`auxillaryEngine.${index}.power_output_rating`}
                                        value={EngineFormik?.values?.auxillaryEngine?.[index]?.power_output_rating || ""}
                                        onChange={(e) => {
                                          EngineFormik?.handleChange(e);
                                        }}
                                        onBlur={(e) => {
                                          EngineFormik?.handleBlur(e);
                                          EngineFormik?.handleChange(e);
                                        }}
                                        disabled={isConfigurationButtonDisabled(vessel)}
                                      />
                                      {
                                        (EngineFormik?.errors?.auxillaryEngine?.[index]?.power_output_rating
                                          ||
                                          EngineFormik?.touched?.auxillaryEngine?.[index]?.power_output_rating
                                        ) && (
                                          env?.form_validation &&
                                          <ErrorTooltip
                                            target={`power_output_rating_${index}`}
                                            message={EngineFormik?.errors?.auxillaryEngine?.[index]?.power_output_rating}
                                            open={!saveBeforeChangePopUp && VesselConfActiveTab === VesselConfigrationTabs.ENGINES && !!(EngineFormik?.errors?.auxillaryEngine?.[index]?.power_output_rating
                                              && EngineFormik?.touched?.auxillaryEngine?.[index]?.power_output_rating)}
                                          />
                                        )}
                                      <div className="input-group-text round_border">kW</div>
                                    </div>
                                    {/* Rated Frequency */}
                                    <Label className="mb-0" for={`rated_frequency_${index}`}>Rated frequency</Label>
                                    <div className="input-group mb-3">
                                      <Input
                                        onFocus={(e) => e.target.select()}
                                        type="text"
                                        id={`rated_frequency_${index}`}
                                        className="form-control max-width-7 text-right"
                                        name={`auxillaryEngine.${index}.rated_frequency`}
                                        value={EngineFormik?.values?.auxillaryEngine?.[index]?.rated_frequency || ""}
                                        onChange={(e) => {
                                          const fieldName = `auxillaryEngine.${index}.rated_frequency`;
                                          checkEmptyStringValue(e, fieldName, EngineFormik?.setFieldValue);
                                        }}
                                        disabled={isConfigurationButtonDisabled(vessel)}
                                      />
                                      {
                                        (EngineFormik?.errors?.auxillaryEngine?.[index]?.rated_frequency ||
                                          EngineFormik?.touched?.auxillaryEngine?.[index]?.rated_frequency
                                        ) &&
                                        env?.form_validation && (
                                          env?.form_validation && (
                                            <ErrorTooltip
                                              target={`rated_frequency_${index}`}
                                              message={EngineFormik?.errors?.auxillaryEngine?.[index]?.rated_frequency}
                                              open={!saveBeforeChangePopUp && VesselConfActiveTab === VesselConfigrationTabs.ENGINES && !!EngineFormik?.errors?.auxillaryEngine?.[index]?.rated_frequency}
                                            />
                                          ))
                                      }
                                      <div className="input-group-text round_border">Hz</div>
                                    </div>
                                    {/* Alternator Power Rating */}
                                    <Label className="mb-0" for={`alternator_power_rating_${index}`}>Alternator power rating</Label>
                                    <div className="input-group mb-3">
                                      <Input
                                        onFocus={(e) => e.target.select()}
                                        type="text"
                                        id={`alternator_power_rating_${index}`}
                                        className="form-control max-width-7 text-right"
                                        name={`auxillaryEngine.${index}.alternator_power_rating`}
                                        value={EngineFormik?.values?.auxillaryEngine?.[index]?.alternator_power_rating || ""}
                                        onChange={(e) => {
                                          const fieldName = `auxillaryEngine.${index}.alternator_power_rating`;
                                          checkEmptyStringValue(e, fieldName, EngineFormik?.setFieldValue);
                                        }}
                                        disabled={isConfigurationButtonDisabled(vessel)}
                                      />
                                      {
                                        (EngineFormik?.errors?.auxillaryEngine?.[index]?.alternator_power_rating ||
                                          EngineFormik?.touched?.auxillaryEngine?.[index]?.alternator_power_rating) &&
                                        env?.form_validation && (
                                          env?.form_validation && (
                                            <ErrorTooltip
                                              target={`alternator_power_rating_${index}`}
                                              message={EngineFormik?.errors?.auxillaryEngine?.[index]?.alternator_power_rating}
                                              open={!saveBeforeChangePopUp && VesselConfActiveTab === VesselConfigrationTabs.ENGINES && !!EngineFormik?.errors?.auxillaryEngine?.[index]?.alternator_power_rating}
                                            />
                                          ))
                                      }
                                      <div className="input-group-text round_border">kVA</div>
                                    </div>
                                  </Col>
                                  <Col>
                                    {/* Rated Voltage */}
                                    <Label className="mb-0" for={`rated_voltage_${index}`}>Rated voltage</Label>
                                    <div className="input-group mb-3">
                                      <Input
                                        onFocus={(e) => e.target.select()}
                                        type="text"
                                        id={`rated_voltage_${index}`}
                                        className="form-control max-width-7 text-right"
                                        name={`auxillaryEngine.${index}.rated_voltage`}
                                        value={EngineFormik?.values?.auxillaryEngine?.[index]?.rated_voltage || ""}
                                        onChange={(e) => {
                                          const fieldName = `auxillaryEngine.${index}.rated_voltage`;
                                          checkEmptyStringValue(e, fieldName, EngineFormik?.setFieldValue);
                                        }}
                                        disabled={isConfigurationButtonDisabled(vessel)}
                                      />
                                      {
                                        (EngineFormik?.errors?.auxillaryEngine?.[index]?.rated_voltage ||
                                          EngineFormik?.touched?.auxillaryEngine?.[index]?.rated_voltage) &&
                                        env?.form_validation && (
                                          <ErrorTooltip
                                            target={`rated_voltage_${index}`}
                                            message={EngineFormik?.errors?.auxillaryEngine?.[index]?.rated_voltage}
                                            open={!saveBeforeChangePopUp && VesselConfActiveTab === VesselConfigrationTabs.ENGINES && !!EngineFormik?.errors?.auxillaryEngine?.[index]?.rated_voltage}
                                          />
                                        )}
                                      <div className="input-group-text round_border">V</div>
                                    </div>
                                    {/* Rated RPM */}
                                    <Label className="mb-0" for={`rated_rpm_${index}`}>Rated RPM</Label>
                                    <div className="input-group mb-3">
                                      <Input
                                        onFocus={(e) => e.target.select()}
                                        type="text"
                                        id={`rated_rpm_${index}`}
                                        className="form-control max-width-7 text-right"
                                        name={`auxillaryEngine.${index}.rated_rpm`}
                                        value={EngineFormik?.values?.auxillaryEngine?.[index]?.rated_rpm || ""}
                                        onChange={(e) => {
                                          const fieldName = `auxillaryEngine.${index}.rated_rpm`;
                                          checkEmptyStringValue(e, fieldName, EngineFormik?.setFieldValue);
                                        }}
                                        disabled={isConfigurationButtonDisabled(vessel)}
                                      />
                                      {
                                        (EngineFormik?.errors?.auxillaryEngine?.[index]?.rated_rpm ||
                                          EngineFormik?.touched?.auxillaryEngine?.[index]?.rated_rpm) &&
                                        env?.form_validation && (
                                          <ErrorTooltip
                                            target={`rated_rpm_${index}`}
                                            message={EngineFormik?.errors?.auxillaryEngine?.[index]?.rated_rpm}
                                            open={!saveBeforeChangePopUp && VesselConfActiveTab === VesselConfigrationTabs.ENGINES &&
                                              !!EngineFormik?.errors?.auxillaryEngine?.[index]?.rated_rpm}
                                          />
                                        )}
                                    </div>
                                    {/* Engine Category */}
                                    <React.Fragment>
                                      <Label
                                        className={`mb-0 ${(vesselFuelIds?.includes(FuelTypes.LNG_BUNKER) ||
                                          vesselFuelIds?.includes(FuelTypes.LNG_BUNKER)) &&
                                          "asteric"
                                          }`}
                                        for={`engine_type_${index}`}
                                      >
                                        Engine category
                                      </Label>
                                      <Select
                                        name={`auxillaryEngine.${index}.engine_type`}
                                        inputId={`engine_type_${index}`}
                                        options={
                                          EngineFormik?.values && EngineFormik?.values?.mainEngine[index]?.engine_type === '2_stroke' ?
                                            EngineTypeMaster2Stroke :
                                            EngineTypeMaster4Stroke
                                        }
                                        getOptionLabel={(
                                          e: any
                                        ) => e.name}
                                        getOptionValue={(e: any) =>
                                          e.id
                                        }
                                        onChange={(e: any) => {
                                          EngineFormik?.setFieldValue(
                                            `auxillaryEngine.${index}.engine_type`,
                                            e?.id
                                          );
                                          EngineFormik?.setFieldValue(
                                            `auxillaryEngine.${index}.engine_type_name`,
                                            e?.name
                                          );
                                        }}
                                        defaultValue={
                                          auxEngine?.engine_type && {
                                            id: EngineFormik?.values?.auxillaryEngine[index]?.engine_type,
                                            name: EngineFormik?.values?.auxillaryEngine[index]?.engine_type_name,
                                          }
                                        }
                                        styles={customStyle}
                                        className="select-height max-width-13"
                                        isDisabled={auxEngine?.machinery_precedence_id === VesselMachineryConstant.STEAM_TURBINE_GENERATOR || isConfigurationButtonDisabled(vessel)}
                                      />
                                      {
                                        (EngineFormik?.errors?.auxillaryEngine?.[index]?.engine_type ||
                                          EngineFormik?.touched?.auxillaryEngine?.[index]?.engine_type) &&
                                        env?.form_validation && (
                                          <ErrorTooltip
                                            target={`engine_type_${index}`}
                                            message={EngineFormik?.errors?.auxillaryEngine?.[index]?.engine_type}
                                            open={!saveBeforeChangePopUp && VesselConfActiveTab === VesselConfigrationTabs.ENGINES &&
                                              !!EngineFormik?.errors?.auxillaryEngine?.[index]?.engine_type}
                                          />
                                        )}
                                    </React.Fragment>
                                  </Col>
                                </Row>
                              </CardBody>
                              <CardFooter className="p-2">
                                <Button
                                  type="button"
                                  color="primary"
                                  className="justify_right "
                                  onClick={async () => {
                                    setSpinnerIndex(index);
                                    setAuxEngineSpinner(true);

                                    // Mark all fields in the target auxillaryEngine[index] as touched
                                    const auxEngineFields = Object.keys(EngineFormik.values.auxillaryEngine[index] || {});
                                    auxEngineFields.forEach(field => {
                                      EngineFormik.setFieldTouched(`auxillaryEngine[${index}].${field}`, true, false);
                                    });
                                    // Run full validation
                                    await EngineFormik.validateForm();
                                    const error = EngineFormik.errors?.auxillaryEngine?.[index];
                                    if (error && Object.keys(error).length > 0) {
                                      setAuxEngineSpinner(false);
                                      return; // Don't submit if there are errors
                                    }
                                    const res: any = await handleEngineSubmit(
                                      EngineFormik.values.auxillaryEngine[index],
                                      "auxillary_machine_setting",
                                      "AuxillaryEngine",
                                      error
                                    );
                                    if (res && res.data) {
                                      const updatedAuxillaryEngine = [...EngineFormik.values.auxillaryEngine];
                                      updatedAuxillaryEngine[index] = res.data;
                                      EngineFormik.setValues({
                                        ...EngineFormik.values,
                                        auxillaryEngine: updatedAuxillaryEngine,
                                      });
                                      await queryClient.invalidateQueries(queryKeyes.vessel.AuxillaryMachineSetting.key);
                                      await queryClient.invalidateQueries(queryKeyes.vessel.ConfigurationList.key);
                                    }
                                    setAuxEngineSpinner(false);
                                  }}
                                  disabled={isConfigurationButtonDisabled(vessel) || (index === spinnerIndex && auxEngineSpinner)}
                                >
                                  {auxEngineSpinner && index === spinnerIndex && <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />}Save
                                </Button>
                              </CardFooter>
                            </Card>
                          </Col>
                        ))) : (
                      <Message message='No Auxiliary Engine data available. Please add auxiliary engine details.' type='warning' width='35rem' />
                    )}
                  </Row>
                </CardBody>
              </Card>
            </React.Fragment>
            <Row className="mt-2">
              <FormValuesDebug
                values={[
                  EngineFormik?.values,
                  EngineFormik?.errors,
                  // EngineFormik.initialValues,
                  // EngineFormik?.touched,
                ]}
              />
            </Row>
          </Form>
        }
        <p>- Set pre-defined engine parameters as per EEDI/EEXI Technical files.</p>
      </React.Fragment >
    );
  };
}
export default Engines;